import React from 'react';
import moment from 'moment';
import { Link } from 'react-router-dom';
import { Alert } from 'react-bootstrap/lib';
import {
  BootstrapTable,
  Options,
  TableHeaderColumn,
} from 'react-bootstrap-table';
import { UpstartLendingStates } from '@upstart/harmony/lib/legos/financingTypes';
import { RicStateSchemaSummary } from 'hooks/services';
import { useRicStateSchemas } from 'hooks/api/ricStateSchemas';
import SidebarContainer from '../SidebarContainer';
import './RicSchemaIndex.scss';

const LENDING_STATE_COUNT = Object.keys(UpstartLendingStates).length;

const TABLE_OPTIONS: Options = {
  defaultSortName: 'stateCode',
  defaultSortOrder: 'asc',
};
const TABLE_STYLES = {
  border: 'none',
};

/**
 * The cell formatters return an element rather than a string deliberately:
 * react-bootstrap-table 4.3.1 renders a non-element `dataFormat` return through
 * `dangerouslySetInnerHTML` (CVE-2021-23398, and 4.3.1 is the package's final
 * release, so there is nothing to upgrade to). An element takes the escaped path.
 */
const formatLastEditedAt = (cell: unknown, summary: RicStateSchemaSummary) => (
  <span>
    {summary.lastEditedAt
      ? moment(summary.lastEditedAt).format('llll')
      : 'No edits yet'}
  </span>
);

/**
 * An edited state has no editor when the write came from the seed script, which is
 * every state until an engineer saves one, so the two nulls read differently.
 */
const formatLastEditedBy = (cell: unknown, summary: RicStateSchemaSummary) => {
  if (!summary.lastEditedAt) {
    return <span>—</span>;
  }
  return <span>{summary.lastEditedBy ?? 'Script (no user)'}</span>;
};

/**
 * The response type declares `contentHash` as required, but `openapi-fetch` does no
 * runtime validation, so a backend that has not shipped the field yet returns a row
 * without it. Rendering that as an empty cell would read as a hash matching the other
 * environment's empty cell, which is the false agreement this column exists to rule out.
 */
const formatContentHash = (cell: unknown, summary: RicStateSchemaSummary) => {
  if (!summary.contentHash) {
    return <span>Unavailable</span>;
  }
  return <span className="ric-schema-content-hash">{summary.contentHash}</span>;
};

const EditCell = (cell: unknown, summary: RicStateSchemaSummary) => (
  <Link
    className="btn btn-default btn-sm"
    to={`/admin/ric-schema/${summary.stateCode}`}
  >
    Edit
  </Link>
);

function RicSchemaIndex() {
  const { stateSchemas, isLoadingStateSchemas, stateSchemasError } =
    useRicStateSchemas();

  const renderBody = () => {
    if (isLoadingStateSchemas) {
      return <Alert>Loading...</Alert>;
    }

    if (stateSchemasError) {
      return (
        <Alert bsStyle="danger">
          Could not load RIC schemas: {stateSchemasError.message}
        </Alert>
      );
    }

    if (!stateSchemas.length) {
      return (
        <Alert bsStyle="warning">
          No RIC schemas found in this environment. Has the seed script run?
        </Alert>
      );
    }

    return (
      <>
        {stateSchemas.length < LENDING_STATE_COUNT && (
          <Alert bsStyle="warning">
            Showing {stateSchemas.length} of {LENDING_STATE_COUNT} lending
            states. The rest have no row in this environment.
          </Alert>
        )}
        <BootstrapTable
          data={stateSchemas}
          hover
          striped
          bodyContainerClass="list-body-container"
          tableStyle={TABLE_STYLES}
          options={TABLE_OPTIONS}
        >
          <TableHeaderColumn
            dataField="stateCode"
            isKey
            dataSort
            filter={{ type: 'TextFilter', defaultValue: '' }}
          >
            State
          </TableHeaderColumn>
          <TableHeaderColumn
            dataField="lastEditedAt"
            dataSort
            dataFormat={formatLastEditedAt}
          >
            Last Edited At
          </TableHeaderColumn>
          <TableHeaderColumn
            dataField="lastEditedBy"
            dataSort
            dataFormat={formatLastEditedBy}
          >
            Last Edited By
          </TableHeaderColumn>
          <TableHeaderColumn
            dataField="contentHash"
            dataFormat={formatContentHash}
          >
            Content Hash
          </TableHeaderColumn>
          <TableHeaderColumn
            dataField="button"
            columnClassName="list-action-column"
            dataFormat={EditCell}
          />
        </BootstrapTable>
      </>
    );
  };

  return (
    <SidebarContainer title="RIC Schemas">
      <div className="dealers-table">
        <div className="dealer-list-container">{renderBody()}</div>
      </div>
    </SidebarContainer>
  );
}

export default RicSchemaIndex;
